import './index.css'

import get from 'lodash/get'
import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate } from 'react-router-dom'

import api from '@disco/api/server'
import {
  CUSTOM_FORMAT_SETTINGS,
  PASSWORD_PROTECTION,
  SESSION_BOUND_URLS,
  URL_EXPIRY_DATE,
  WATERMARKING,
} from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  resetPlaylistShareForm,
  updatePlaylistShareForm,
} from '@disco/state-management/actions/FormActions'
import {
  getPlaylistTracks,
  loadPlaylistZipUrls,
} from '@disco/state-management/actions/playlist-actions'
import {
  editContactField,
  getPlaylistShares,
  playlistShare,
} from '@disco/state-management/actions/PlaylistShareActions'
import { loadBatch } from '@disco/state-management/actions/PlaylistTrackActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  makeGetFeatureData,
  makeIsFeatureEnabled,
  PRO,
} from '@disco/state-management/selectors/business'
import { playlistSharesWithContacts } from '@disco/state-management/selectors/shares'
import {
  getBusinessEmailTemplates,
  getUser,
} from '@disco/state-management/selectors/user'
import captureError, { parseServerError } from '@disco/utils/captureError'
import type { WatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import { getWatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import { pushUINotification } from '@disco/utils/notifications'
import { getPlaylistDefaultDownloadLink } from '@disco/utils/playlists'

import type { GroupId } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { ErrorsObject } from 'types/server-error'
import type { User } from 'types/user'

import PlaylistInfo from 'components/common/playlist-info'
import { Button, Spinner, Tab, Tabs } from 'components/ui'
import { Flex } from 'components/ds'
import Alert from 'components/ui/alert'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import ModalWindow from 'components/ui/modal-window'
import PricingBadge from 'components/ui/pricing-badge'

// TODO: CopyTracklist should be moved to components
import CopyTracklist from 'pages/explorer/shortlists/saved/copy-tracklist'

import type { RootState } from 'store/configureStore'

import CopyUrlTab from './copy-url-tab'
import EmbedTab from './embed-tab'
import PlaylistShareTab from './playlist-share-tab'
import SecurityTab from './security-tab'

function mapStateToProps(state: RootState) {
  const isFeatureEnabled1 = makeIsFeatureEnabled()
  const isFeatureEnabled2 = makeIsFeatureEnabled()
  const isFeatureEnabled3 = makeIsFeatureEnabled()
  const getFeatureData = makeGetFeatureData()
  const getFeatureData2 = makeGetFeatureData()
  return (state: RootState, ownProps: { playlist: Playlist }) => {
    const { playlist, sharesLoaded, sharesItems } = playlistSharesWithContacts(
      state,
      {
        id: ownProps.playlist.playlist_id,
      },
    )
    const watermarkingEnabled = isFeatureEnabled1(state, {
      featureName: WATERMARKING,
    })
    const { isFeatureEnabled: passwordProtectionEnabled, isTrial } =
      getFeatureData2(state, {
        featureName: PASSWORD_PROTECTION,
      })
    const shareSecurityEnabled = [
      {
        isFeatureEnabled: passwordProtectionEnabled,
      },
      {
        isFeatureEnabled: isFeatureEnabled2(state, {
          featureName: URL_EXPIRY_DATE,
        }),
      },
    ].some(({ isFeatureEnabled }) => isFeatureEnabled)
    const { isFeatureEnabled } = getFeatureData(state, {
      featureName: CUSTOM_FORMAT_SETTINGS,
    })
    return {
      isPhone: isDeviceTypePhone(state),
      sharesLoaded,
      sharesItems,
      playlist,
      shareSecurityEnabled,
      sessionBoundURLsEnabled: isFeatureEnabled3(state, {
        featureName: SESSION_BOUND_URLS,
      }),
      emailTemplates: getBusinessEmailTemplates(state),
      user: getUser(state),
      // Note: Optional chaining used for business properties - if business is
      // null/undefined, these will be undefined which is handled gracefully by
      // consuming components (PasswordWarning treats undefined as false)
      disablePublicDownloads:
        state.user.business?.private_metadata.disable_public_downloads,
      autoEmailSharedPlaylistPasswords:
        state.user.business?.auto_email_shared_playlist_passwords,
      ...state.forms.playlistShareForm,
      isTrial,
      watermarkingEnabled,
      watermarkingQuota: getWatermarkingQuota(state),
      isFeatureEnabled,
    }
  }
}

const mapDispatchToProps = {
  resetPlaylistShareForm,
  updatePlaylistShareForm,
  getPlaylistTracks,
  loadPlaylistZipUrls,
  editContactField,
  sendMixpanelEvent,
  playlistShare,
  getPlaylistShares,
  sendGenericError,
  loadBatch,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type ReduxProps = ConnectedProps<typeof connector>

type OwnProps = {
  onClose: (...args: Array<any>) => any
  emailTemplates:
    | {
        id: string
        name: string
      }[]
    | null
    | undefined
  onShare: (...args: Array<any>) => any
  playlist: Playlist
  playlistBaseObjErrorMessage?: ErrorsObject
  navigate: NavigateFunction
}

type Props = ReduxProps & OwnProps

type State = {
  tabIndex: number
  publicUrl: string
  internalUrl: string
  zipUrl: string
  embedUrl: string
  disabledShares: Record<number, boolean>
  playlistBaseObj: Record<string, any> | null | undefined
  isLoading: boolean
  playlist: Playlist | null | undefined
  embedParams: {
    download: boolean
    color?: string
  }
  playlistBaseObjErrorMessage: ErrorsObject | null | undefined
}

class PlaylistShareDialog extends Component<Props, State> {
  state: State = {
    tabIndex: 0,
    publicUrl: '',
    internalUrl: '',
    zipUrl: '',
    embedUrl: '',
    disabledShares: {},
    playlistBaseObj: null,
    isLoading: false,
    playlist: null,
    embedParams: {
      download: Boolean(
        this.props.playlist.download_links &&
          !this.props.disablePublicDownloads,
      ),
    },
    playlistBaseObjErrorMessage: null,
  }

  componentDidMount() {
    if (
      !this.props.territories &&
      this.props.user.business.private_metadata.territories_enabled
    ) {
      // need to init territories, leave fetching for componentWillReceiveProps
      this.props.updatePlaylistShareForm({
        territories: this.props.user.territories,
      })
    } else {
      this._fetchTabsData(this.props)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { playlist: currentPlaylist, territories } = this.props

    if (
      nextProps.playlist.id !== currentPlaylist.id || // change in territories require embed reload
      nextProps.territories !== territories
    ) {
      this._fetchTabsData(nextProps)
    } else if (
      nextProps.playlist.default_zip_mode !==
        currentPlaylist.default_zip_mode ||
      (nextProps.playlist.zip_urls && !currentPlaylist.zip_urls)
    ) {
      this._fetchUrls(nextProps.playlist)
    }
  }

  componentWillUnmount() {
    this.props.resetPlaylistShareForm()
  }

  async _fetchUrls(playlist: Playlist) {
    try {
      const {
        hostname,
        private_metadata: { territories_enabled },
      } = this.props.user.business
      let publicUrl = playlist.view_playlist_url
      const playlistId = playlist.playlist_id
      let internalUrl = `https://${hostname}/explorer/playlists/${playlistId}`
      // if it's not territories enabled business we can try reusing zip urls
      const zip_urls =
        !playlist.zip_urls || territories_enabled
          ? await this.props.loadPlaylistZipUrls(playlist)
          : playlist.zip_urls
      const playlistWithZips = { ...playlist, zip_urls }
      let zipUrl =
        getPlaylistDefaultDownloadLink(playlistWithZips, 'public') || ''
      this.setState({
        publicUrl,
        internalUrl,
        zipUrl,
      })

      if (zipUrl) {
        const [p, i, z] = await api.shorturl([publicUrl, internalUrl, zipUrl])
        publicUrl = p ?? publicUrl
        internalUrl = i ?? internalUrl
        zipUrl = z ?? zipUrl
      } else {
        const [p, i] = await api.shorturl([publicUrl, internalUrl])
        publicUrl = p ?? publicUrl
        internalUrl = i ?? internalUrl
      }

      this.setState({
        publicUrl,
        internalUrl,
        zipUrl,
      })
    } catch (error) {
      this.props.sendGenericError(error)
    }
  }

  _fetchTracks(props: Props): Promise<Playlist> {
    return props.getPlaylistTracks(
      props.playlist,
      props.territories || undefined,
    )
  }

  async _fetchEmbeds({ playlist, territories }: any, params: any) {
    const embedUrl = await api.getEmbeddedPlaylistUrl(playlist.playlist_id, {
      ...params,
      territory_ids: territories ? JSON.stringify(territories) : undefined,
    })
    this.setState({
      embedUrl,
    })
  }

  async _fetchPlaylistSecurity(props: Props) {
    let obj = {}

    try {
      if (props.shareSecurityEnabled) {
        obj = await api.getPlaylistBaseObj(props.playlist.playlist_id)
        this.setState({
          playlistBaseObj: obj,
        })
      }
    } catch (error) {
      captureError(error, {
        description: 'Error in _fetchPlaylistSecurity()',
      })
      this.setState({
        playlistBaseObjErrorMessage: error,
      })
    }
  }

  async _fetchTrackAndUrls(props: Props) {
    // only in case of territories enabled business we need to await
    // updated playlist data
    if (props.user.business.private_metadata.territories_enabled) {
      this.setState({
        isLoading: true,
      })
      const playlist: Playlist = await this._fetchTracks(props)
      // tab 2 should wait till we get full playlist as when we fetch full playlist
      // we set territories
      await this._fetchUrls(playlist)
      this.setState({
        isLoading: false,
        playlist,
      })
    } else {
      // for business with territories disabled, we don't need to wait and can
      // fetch data concurrently
      this._fetchTracks(props)

      this._fetchUrls(props.playlist)
    }
  }

  _fetchTabsData = (props: Props) => {
    return Promise.all([
      // tab 1
      props.getPlaylistShares(props.playlist.playlist_id), // tab 4
      this._fetchEmbeds(props, this.state.embedParams), // tab 5
      this._fetchPlaylistSecurity(props), // tab 2/3
      this._fetchTrackAndUrls(props),
    ])
  }
  _handleTabChange = (tabIndex: number) => {
    this.setState({
      tabIndex,
    })
  }
  _handleCopyTracklist = (message?: string) => {
    this.props.sendMixpanelEvent('Copy List tab - Copy tracklist')

    if (message) {
      pushUINotification({
        level: 'success',
        message,
      })
    }
  }
  _handleEmbedParamsChange = (params: any) => {
    this.setState(
      {
        embedParams: { ...this.state.embedParams, ...params },
      },
      () => this._fetchEmbeds(this.props, this.state.embedParams),
    )
  }

  _handleCopyPublicUrl = () => {
    const { publicUrl } = this.state
    if (publicUrl) {
      navigator.clipboard
        .writeText(publicUrl)
        .then(() => {
          pushUINotification({
            level: 'success',
            message: 'Copied public link to clipboard',
          })
          this.props.sendMixpanelEvent('Share Modal - Copy public link')
        })
        .catch(() => {
          pushUINotification({
            level: 'error',
            message: 'Failed to copy link to clipboard',
          })
        })
    }
  }

  _renderBadge() {
    const { shareSecurityEnabled, isTrial } = this.props

    if (!shareSecurityEnabled || isTrial) {
      return (
        <div className="PlaylistShareDialog_BadgeWrapper">
          <div className="PlaylistShareDialog_Badge">
            <PricingBadge title={PRO} />
          </div>
        </div>
      )
    }
  }

  _renderModalContent() {
    const {
      isPhone,
      isTrial,
      watermarkingEnabled,
      watermarkingQuota,
      isFeatureEnabled,
    } = this.props
    const desktopLayout = !isPhone
    const playlist = this.state.playlist || this.props.playlist

    if (this.state.isLoading) {
      return (
        <div className="EmailForm">
          <Spinner />
        </div>
      )
    }

    const sharesError = this.props.playlist.shares?.errorMessage
    return (
      <>
        {!this.props.isPhone && (
          <div className="PlaylistShareDialog_HeadingSubtitleContainer">
            <Flex align="center" gap="2" width="100%">
              <Flex align="center" gap="2" flexGrow="1" minWidth="0">
                <PlaylistInfo playlist={this.props.playlist} />
              </Flex>
              <Button
                appearance="icon"
                icon="link"
                onClick={this._handleCopyPublicUrl}
                tooltip="Copy public link"
              />
              <Button
                appearance="icon"
                icon="open-in-new-tab"
                link={this.state.publicUrl}
                linkTarget="_blank"
                tooltip="Open public link in new tab"
              />
            </Flex>
          </div>
        )}
        {this.props.user.business.private_metadata.territories_enabled && (
          <div className="EmailForm__warning warning-block">
            Warning! The playlist will be filtered by territories before
            it&#39;s sent. See{' '}
            <InlineLink
              to={this.state.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              preview
            </InlineLink>
            .
          </div>
        )}
        <div className="ShortlistShareModal">
          {sharesError && (
            <div className="PlaylistShareDialog_AlertContainer">
              <Alert icon="warning" status="warning">
                {sharesError}
              </Alert>
            </div>
          )}
          {this.props.playlist.watermark &&
            this.props.playlist.watermarking_status === 'processing' && (
              <div className="PlaylistShareDialog_AlertContainer">
                <Alert status="danger" icon="warning">
                  This playlist is still being watermarked.
                </Alert>
              </div>
            )}
          <Tabs
            className="Tabs--modal"
            index={this.state.tabIndex}
            onChange={this._handleTabChange}
          >
            <Tab label="Share to contact">
              <PlaylistShareTab
                onClose={this.props.onClose}
                onShare={this.props.onShare}
                subject={this.props.subject}
                message={this.props.message}
                territories={this.props.territories}
                shareFunction={this.props.playlistShare}
                emailTemplates={this.props.emailTemplates}
                user={this.props.user}
                recipients={this.props.recipients}
                shareObject={playlist}
                sharesItems={this.props.sharesItems}
                sharesLoaded={this.props.sharesLoaded}
                sharing={this.props.sharing}
                longTabNames={desktopLayout}
                navigate={this.props.navigate}
                sendMixpanelEvent={this.props.sendMixpanelEvent}
                pushUINotification={pushUINotification}
                editContactField={this.props.editContactField}
                updatePlaylistShareForm={this.props.updatePlaylistShareForm}
                disabledShares={this.state.disabledShares}
                setShareDisabled={(recipient, val) => {
                  this.setState({
                    disabledShares: {
                      ...this.state.disabledShares,
                      [recipient.id]: val,
                    },
                  })
                }}
                playlistPassword={this.state.playlistBaseObj?.password}
                autoEmailSharedPlaylistPasswords={
                  this.props.autoEmailSharedPlaylistPasswords
                }
              />
            </Tab>
            <Tab label="Copy URL">
              <CopyUrlTab
                onClose={this.props.onClose}
                publicUrl={this.state.publicUrl}
                internalUrl={this.state.internalUrl}
                zipUrl={
                  this.props.disablePublicDownloads ? null : this.state.zipUrl
                }
                sendMixpanelEvent={this.props.sendMixpanelEvent}
                playlistId={this.props.playlist.playlist_id}
                playlistName={this.props.playlist.name}
                watermark={this.props.playlist.watermark}
                watermarkingStatus={this.props.playlist.watermarking_status}
                downloadsDisabledDueTo={
                  this.props.sessionBoundURLsEnabled
                    ? 'Disabled due to security settings'
                    : null
                }
              />
            </Tab>
            {desktopLayout && (
              <Tab label="Copy List">
                <CopyTracklist
                  format={get(
                    this.props.user,
                    'business.public_metadata.tracklist_format',
                    null,
                  )}
                  shortlist={
                    {
                      ...playlist,
                      items: playlist.tracks.map(track => ({
                        track: track,
                      })),
                    } as any
                  }
                  user={this.props.user}
                  onClose={this.props.onClose}
                  onCopy={this._handleCopyTracklist}
                />
              </Tab>
            )}
            {desktopLayout && (
              <Tab label="Embed">
                <EmbedTab
                  iframeId={`disco-playlist-${playlist.playlist_id}`}
                  embedUrl={this.state.embedUrl}
                  embedParams={this.state.embedParams}
                  onParamsChange={this._handleEmbedParamsChange}
                  onClose={this.props.onClose}
                  type="playlist"
                  disablePublicDownloads={Boolean(
                    this.props.disablePublicDownloads,
                  )}
                  watermark={playlist.watermark}
                />
              </Tab>
            )}
            {desktopLayout && (
              <Tab label={<>Security {this._renderBadge()}</>}>
                {this.state.playlistBaseObj ? (
                  <SecurityTab
                    playlistBaseObj={this.state.playlistBaseObj}
                    setPlaylistBaseObj={changes => {
                      this.setState({
                        playlistBaseObj: {
                          ...this.state.playlistBaseObj,
                          ...changes,
                        },
                      })
                      this.props.loadBatch([{ ...playlist, ...changes }])
                    }}
                    shareSecurityEnabled={this.props.shareSecurityEnabled}
                    user={this.props.user}
                    pushUINotification={pushUINotification}
                    sendMixpanelEvent={this.props.sendMixpanelEvent}
                    isTrial={isTrial}
                    watermarkingEnabled={watermarkingEnabled}
                    watermarkingQuota={watermarkingQuota}
                    // @ts-expect-error check if needed
                    customFormatsEnabled={isFeatureEnabled}
                  />
                ) : (
                  <>
                    {this.state.playlistBaseObjErrorMessage && (
                      <Alert status="warning" icon="warning">
                        {parseServerError(
                          this.state.playlistBaseObjErrorMessage,
                        )}
                      </Alert>
                    )}
                  </>
                )}
              </Tab>
            )}
          </Tabs>
        </div>
      </>
    )
  }

  render() {
    return (
      <ModalWindow
        header={<Heading size="700">Share Playlist</Heading>}
        isOpen
        width="medium"
        onClose={this.props.onClose}
      >
        {this._renderModalContent()}
      </ModalWindow>
    )
  }
}

const ConnectedPlaylistShareDialog = connector(PlaylistShareDialog)

function PlaylistShareDialogWithNavigate(props) {
  const navigate = useNavigate()
  return <ConnectedPlaylistShareDialog {...props} navigate={navigate} />
}

export default PlaylistShareDialogWithNavigate
